import moment from 'moment-timezone'

import { formatTime, parseTime } from 'services/time_format'

import { CUSTOM_RULE_ID, DEFAULT_AVAILABLE_DAYS, DEFAULT_INTERVAL } from './constants'
import { validateIntervals } from './wday_rule/utils'

export const getDefaultIntervals = () => [DEFAULT_INTERVAL]

export const getDefaultRules = () => DEFAULT_AVAILABLE_DAYS.map((wday) => ({
  type: 'wday',
  wday,
  intervals: getDefaultIntervals()
}))

export const getOwnRuleId = ({ own_availability_rule: ownRule }) => {
  if (!ownRule) return CUSTOM_RULE_ID
  if (!ownRule.id) return CUSTOM_RULE_ID

  return ownRule.id
}

export const encodeAvailabilityRule = (wdayRules, dateRules, timezone) => {
  const rules = Object.entries(wdayRules).map(([key, value]) => {
    if (!value.length) return undefined
    return {
      type: 'wday',
      wday: key,
      intervals: value
    }
  }).filter(Boolean).concat(dateRules)

  return { rules, timezone }
}

export const getRule = (primaryMember, eventType) => {
  const { active_availability_rule_id: activeAvailabilityRuleId, own_availability_rule: ownAvailabilityRule } = eventType

  if (activeAvailabilityRuleId) {
    return ownAvailabilityRule
  }

  if (!primaryMember) return null
  const { availableHours, dateOverrides, timezone } = primaryMember

  if (availableHours && dateOverrides && timezone) {
    return encodeAvailabilityRule(availableHours, dateOverrides, timezone)
  }

  return getDefaultRules()
}

const getNextInterval = (intervals) => {
  const lastInterval = intervals[intervals.length - 1]
  const endTime = parseTime(lastInterval.to)

  return {
    from: formatTime(endTime.clone().add(1, 'hours'), '24h'),
    to: formatTime(endTime.clone().add(2, 'hours'), '24h')
  }
}

export const addInterval = (intervals) => {
  if (intervals.length === 0) return getDefaultIntervals()

  return validateIntervals([...intervals, getNextInterval(intervals)])
}

const chronologicalSort = (x, y) => (moment(x.date).isSameOrAfter(moment(y.date)) ? 1 : -1)

export const upsertRules = (dates, dateIds, intervals) => {
  const rulesForSelectedDays = dateIds.map((date) => ({
    type: 'date',
    date,
    intervals
  }))
  const rulesForOtherDays = dates.filter((r) => !dateIds.includes(r.date))

  return [...rulesForOtherDays, ...rulesForSelectedDays].sort(chronologicalSort)
}

export const removeRules = (dates, dateIds) => dates.filter((r) => !dateIds.includes(r.date))

export const getIntervalsForDate = (dateId, dates, days = {}) => {
  const selectedDate = dates.find(({ type, date }) => type === 'date' && date === dateId)
  if (selectedDate) return selectedDate.intervals

  const recurringIntervals = days[moment(dateId).locale('en').format('dddd').toLowerCase()]
  if (recurringIntervals && recurringIntervals.length > 0) return recurringIntervals

  return null
}

export const formatDoubleBookingRules = (rules) => {
  if (!rules?.length) {
    return []
  }

  return rules.map((r) => {
    const { id, searchTerm, type, _destroy } = r
    return {
      ...(typeof id !== 'string' ? { id } : {}),
      matching_criteria: {
        event_name: searchTerm,
        type
      },
      _destroy
    }
  })
}

export const getAvailabilityRuleReferenceAttributes = (
  availabilityRuleId,
  doubleBookingRules,
  includeAvailabilityRuleAttributes = true) => ({
  ...(includeAvailabilityRuleAttributes) && { availability_rule_id: availabilityRuleId },
  double_booking_rules_attributes: formatDoubleBookingRules(doubleBookingRules)
})
